import { Typography } from "@shared/ui.primitives";

import {
  barContainer,
  barProgress,
  barWithLabelWrapper,
  barWrapper
} from "./ProgressBar.css";

type ProgressBarProps = {
  totalSteps: number;
  currentStep: number;
  label?: string;
};

const toTwoDigits = (number: number) => String(number).padStart(2, "0");

/**
 * Displays a progress bar showing the current step relative to total steps.
 * Supports two modes:
 * - With label: shows label text and progress bar only.
 * - Without label: shows numeric step counts and progress bar.
 *
 * @param {ProgressBarProps} props - Props for the ProgressBar component.
 * @param {number} props.totalSteps - Total number of steps in the progress bar.
 * @param {number} props.currentStep - Current step in the progress bar.
 * @param {string} [props.label] - Optional label for the progress bar.
 * @returns {JSX.Element} The rendered progress bar.
 */
export const ProgressBar = ({
  currentStep,
  totalSteps,
  label
}: ProgressBarProps) => {
  const percentage = Math.min(Math.max(currentStep / totalSteps, 0), 1) * 100;

  return label ? (
    <div className={barWithLabelWrapper}>
      <Typography textAlign="left" variant="body-sm">
        {label}
      </Typography>
      <div className={barWrapper}>
        <div className={barContainer}>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={totalSteps}
            aria-valuenow={Math.min(Math.max(currentStep, 0), totalSteps)}
            className={barProgress}
            data-testid="progress-bar"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  ) : (
    <div className={barWrapper}>
      <Typography variant="subheader-6">{toTwoDigits(currentStep)}</Typography>
      <div className={barContainer}>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={totalSteps}
          aria-valuenow={Math.min(Math.max(currentStep, 0), totalSteps)}
          className={barProgress}
          data-testid="progress-bar"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <Typography variant="subheader-6">{toTwoDigits(totalSteps)}</Typography>
    </div>
  );
};
